/**
 * UX Audit Phase 2 (Task #2050) — Partner & Marketplace route slice.
 *
 * Covers: all /partner/* routes, marketplace partner directory.
 */
import { lazy } from "react";
import { Route, Redirect } from "wouter";
import { PartnerRoute, ProtectedRoute } from "@/components/routing/RouteGate";

const PartnerDashboard = lazy(() => import("@/pages/partner/Dashboard"));
const PartnerWorkerList = lazy(() => import("@/pages/partner/WorkerList"));
const PartnerBulkUpload = lazy(() => import("@/pages/partner/BulkUpload"));
const PartnerCertifications = lazy(() => import("@/pages/partner/Certifications"));
const PartnerActiveJobs = lazy(() => import("@/pages/partner/ActiveJobs"));
const PartnerWorkerReadiness = lazy(() => import("@/pages/partner/WorkerReadiness"));
const PartnerVerifications = lazy(() => import("@/pages/partner/Verifications"));
const PartnerAttendancePage = lazy(() => import("@/pages/partner/Attendance"));
const PartnerTimesheetsPage = lazy(() => import("@/pages/partner/Timesheets"));
const PartnerPermits = lazy(() => import("@/pages/partner/Permits"));
const PartnerSafety = lazy(() => import("@/pages/partner/Safety"));
const PartnerDocuments = lazy(() => import("@/pages/partner/Documents"));
const PartnerPlatformHome = lazy(() => import("@/pages/partner/PlatformHome"));
const PartnerAccount = lazy(() => import("@/pages/partner/Account"));
const PartnerAnalytics = lazy(() => import("@/pages/partner/Analytics"));
const PartnerMarketplaceListing = lazy(() => import("@/pages/partner/MarketplaceListing"));
const PartnerAgreementsPage = lazy(() => import("@/pages/partner/Agreements"));
const PartnerTeam = lazy(() => import("@/pages/partner/Team"));
const PartnerBranding = lazy(() => import("@/pages/partner/Branding"));
const PartnerCompliance = lazy(() => import("@/pages/partner/Compliance"));
const PartnerEvidence = lazy(() => import("@/pages/partner/Evidence"));
const PartnerInbox = lazy(() => import("@/pages/partner/Inbox"));
const PartnerAuditLog = lazy(() => import("@/pages/partner/AuditLog"));
const PartnerDirectory = lazy(() => import("@/pages/marketplace/PartnerDirectory"));
const PartnerProfilePage = lazy(() => import("@/pages/marketplace/PartnerProfile"));
const PartnerOverview = lazy(() => import("@/pages/partner/PartnerOverview"));

export function partnerRoutes() {
  return (
    <>
      <Route path="/partner/home">{() => <PartnerRoute allowUnassigned component={PartnerPlatformHome} />}</Route>
      <Route path="/partner/overview">{() => <PartnerRoute component={PartnerOverview} />}</Route>
      <Route path="/partner/dashboard">{() => <PartnerRoute component={PartnerDashboard} />}</Route>
      <Route path="/partner/workers">{() => <PartnerRoute capability="can_deploy_workers" component={PartnerWorkerList} />}</Route>
      <Route path="/partner/upload">{() => <PartnerRoute capability="can_deploy_workers" component={PartnerBulkUpload} />}</Route>
      <Route path="/partner/certs">{() => <PartnerRoute component={PartnerCertifications} />}</Route>
      <Route path="/partner/jobs">{() => <PartnerRoute component={PartnerActiveJobs} />}</Route>
      <Route path="/partner/readiness">{() => <PartnerRoute component={PartnerWorkerReadiness} />}</Route>
      <Route path="/partner/verifications">{() => <PartnerRoute capability="can_verify_worker_identity" component={PartnerVerifications} />}</Route>
      <Route path="/partner/permits">{() => <PartnerRoute capability={["can_request_ptw", "can_create_ptw", "can_approve_ptw"]} component={PartnerPermits} />}</Route>
      <Route path="/partner/attendance">{() => <PartnerRoute component={PartnerAttendancePage} />}</Route>
      <Route path="/partner/timesheets">{() => <PartnerRoute component={PartnerTimesheetsPage} />}</Route>
      <Route path="/partner/safety">{() => <PartnerRoute capability={["can_report_incidents", "can_own_investigation", "can_raise_non_conformances", "can_audit_only", "is_audit_only"]} component={PartnerSafety} />}</Route>
      <Route path="/partner/documents">{() => <PartnerRoute component={PartnerDocuments} />}</Route>
      <Route path="/partner/account">{() => <PartnerRoute component={PartnerAccount} />}</Route>
      <Route path="/partner/analytics">{() => <PartnerRoute component={PartnerAnalytics} />}</Route>
      <Route path="/partner/marketplace">{() => <PartnerRoute component={PartnerMarketplaceListing} />}</Route>
      <Route path="/partner/agreements">{() => <PartnerRoute component={PartnerAgreementsPage} />}</Route>
      <Route path="/partner/team">{() => <PartnerRoute allowUnassigned component={PartnerTeam} />}</Route>
      <Route path="/partner/branding">{() => <PartnerRoute permission="company.manage" component={PartnerBranding} />}</Route>
      <Route path="/partner/compliance">{() => <PartnerRoute component={PartnerCompliance} />}</Route>
      <Route path="/partner/unassigned">{() => <Redirect to="/partner/home" />}</Route>
      <Route path="/partner/evidence">{() => <PartnerRoute allowUnassigned component={PartnerEvidence} />}</Route>
      <Route path="/partner/inbox">{() => <PartnerRoute component={PartnerInbox} />}</Route>
      <Route path="/partner/audit-log">{() => <PartnerRoute component={PartnerAuditLog} />}</Route>
      <Route path="/marketplace/partners/:companyId">{() => <ProtectedRoute permission="company.view" component={PartnerProfilePage} />}</Route>
      <Route path="/marketplace/partners">{() => <ProtectedRoute permission="company.view" component={PartnerDirectory} />}</Route>
    </>
  );
}
